import type { EventBus } from './EventBus';
import type { TimerService } from './services/browser/TimerService';
import type { ApiClient } from './api/ApiClient';
import { PayloadBuilder, buildUserBlock, type HeartbeatPayload } from './PayloadBuilder';
import type { PageContextService } from './services/PageContextService';
import type { MeasurementStore } from './services/MeasurementStore';
import type { TabService } from './services/TabService';
import type { CustomerService } from './services/CustomerService';
import type { DeviceService } from './services/DeviceService';
import type { TrackingDebugService } from './services/TrackingDebugService';
import { HEARTBEAT_INTERVAL_MS, HEARTBEAT_JITTER_MS, TRACKING_PAYLOAD_SCHEMA_VERSION } from './config/heartbeat';
import { TrackingBusEvent } from './constants/trackingBusEvent';
import { TrackingTag } from './constants/trackingTag';
import { heartbeatScheduleDelayMs, resumePeriodicCadenceDecision, shouldSkipRedundantUnloadFinal } from './heartbeatCadence';

/**
 * Drives periodic collect payloads while the tab is visible and consent is granted.
 *
 * **Idle vs hidden:** `paused` means the tab is hidden (`tab:inactive`). `idleSuspended` means the user
 * has been inactive long enough (`USER_IDLE_THRESHOLD_MS`, see `config/userIdle.ts`) while the tab
 * could still be visible — e.g. tab open with no scroll/input. Tab rotation for that case is handled
 * in `TabService`; this class stops the timer and skips periodic sends until `tab:active` /
 * `tab:activity` fires again.
 *
 * **Two idle checks (same TabService primitive, different jobs):**
 *
 * 1. **Timer idle gate** (`onHeartbeatTimerTick`): runs only when the heartbeat timer fires. If the user
 *    is past the idle threshold, we rotate the tab *without* emitting `tab:reset` on the bus
 *    (`emitTabReset: false`), enter `idleSuspended`, clear the timer and the collect queue, and **do not**
 *    build or send a heartbeat on this tick. That avoids an extra periodic network request right when we
 *    decide to stop heartbeats for idle.
 *
 * 2. **Pre-send idle hook** (`applyIdleHookBeforePayload` → `buildPayload` / `buildPayloadSync`): runs
 *    immediately before any payload is built for a send (periodic, resume, `tab:reset`, unload finals).
 *    If the threshold was exceeded, we rotate tab storage the same way (`emitTabReset: false`) but
 *    **continue** and send — so the payload matches the new tab id / heartbeat index and we do not
 *    nest bus-triggered heartbeats inside `tab:reset`.
 *
 * **Tab resume / unload cadence:** `lastPeriodicHeartbeatSentAtMs` is set when a periodic payload is enqueued
 * (`sendHeartbeat`). Visibility resume and unload final skips reuse this anchor vs `HEARTBEAT_INTERVAL_MS`
 * so tab flip / navigate-away matches close-tab behavior for redundant-send avoidance (see resume/unload helpers).
 */
export class HeartbeatLoop {
  private timerId: number | null = null;
  private running = false;
  private paused = false;
  /** Wall-clock time of last periodic `api.collect` from {@link sendHeartbeat} — drives resume + unload gating. */
  private lastPeriodicHeartbeatSentAtMs: number | null = null;
  /** True after any tab activity since last periodic send — used to decide unload-final redundancy. */
  private activitySinceLastPeriodic = false;
  /** After {@link onHeartbeatTimerTick} hits the timer idle gate: no periodic sends until user activity. */
  private idleSuspended = false;
  /** True after start(): a keepalive final may be needed on page:unload unless stop() already sent one. */
  private unloadFinalPending = false;
  /** One-shot for page:unload (pagehide can fire more than once in edge cases). */
  private pageUnloadFinalSent = false;

  constructor(
    private readonly bus: EventBus,
    private readonly timers: TimerService,
    private readonly api: ApiClient,
    private readonly pageContext: PageContextService,
    private readonly measurements: MeasurementStore,
    private readonly tab: TabService,
    private readonly customer: CustomerService,
    private readonly device: DeviceService,
    private readonly debug?: TrackingDebugService,
    private readonly intervalMs = HEARTBEAT_INTERVAL_MS,
    private readonly jitterMs = HEARTBEAT_JITTER_MS,
    private readonly schemaVersion = TRACKING_PAYLOAD_SCHEMA_VERSION,
  ) {}

  init(): void {
    this.bus.subscribe(
      TrackingBusEvent.TabInactive,
      () => {
        if (!this.running) {
          return;
        }
        this.paused = true;
        this.clearTimer();
        this.debug?.log('HeartbeatLoop', 'paused (tab inactive)', {});
      },
      TrackingTag.HeartbeatLoop,
    );

    // Subscribe before tab:activity resume so TabService tab:active / tab:activity run first (refresh last_active).
    this.bus.subscribe(
      TrackingBusEvent.TabActive,
      () => {
        if (!this.running) {
          return;
        }
        this.paused = false;
        if (this.idleSuspended) {
          this.idleSuspended = false;
          this.debug?.log('HeartbeatLoop', 'resumed from idle suspend (tab:active)', {});
        }
        this.debug?.log('HeartbeatLoop', 'resumed (tab active)', {});
        this.resumePeriodicCadenceAfterVisibility();
      },
      TrackingTag.HeartbeatLoop,
    );

    /** Motionless visible tab: resume periodic sends after idle suspension without a visibility flip. */
    this.bus.subscribe(
      TrackingBusEvent.TabActivity,
      () => {
        this.activitySinceLastPeriodic = true;
        if (!this.running || this.paused || !this.idleSuspended) {
          return;
        }
        this.idleSuspended = false;
        this.debug?.log('HeartbeatLoop', 'resumed from idle suspend (tab:activity)', {});
        this.resumePeriodicCadenceAfterVisibility();
      },
      TrackingTag.HeartbeatLoop,
    );

    this.bus.subscribe<{ tabId: string }>(
      TrackingBusEvent.TabReset,
      () => {
        if (!this.running) {
          return;
        }
        this.idleSuspended = false;
        this.debug?.log('HeartbeatLoop', 'tab:reset → immediate heartbeat', {});
        void this.sendHeartbeat();
        this.scheduleNext();
      },
      TrackingTag.HeartbeatLoop,
    );

    this.bus.subscribe(
      TrackingBusEvent.ConsentWithdrawn,
      () => {
        this.stop({ silent: true });
      },
      TrackingTag.HeartbeatLoop,
    );

    this.bus.subscribe(
      TrackingBusEvent.PageBfcache,
      () => {
        this.pageUnloadFinalSent = false;
        this.debug?.log('HeartbeatLoop', 'page:bfcache → reset pageUnloadFinalSent', {});
      },
      TrackingTag.HeartbeatLoop,
    );

    this.bus.subscribe<{ persisted: boolean }>(
      TrackingBusEvent.PageUnload,
      (payload) => {
        // Always attempt final collect when unloading, including when idleSuspended (product: last signal on leave).
        if (!this.unloadFinalPending || this.pageUnloadFinalSent) {
          return;
        }
        this.pageUnloadFinalSent = true;

        const persisted = payload?.persisted === true;
        if (persisted) {
          const syncPayload = this.buildPayloadSync();
          if (syncPayload) {
            this.debug?.log('HeartbeatLoop', 'page:unload → final (bfcache)', {
              persisted: true,
              syncPayload: true,
              delivery: 'keepalive',
              idleSuspended: this.idleSuspended,
            });
            this.api.collect(syncPayload, { final: true });
          } else {
            this.debug?.log('HeartbeatLoop', 'page:unload → final (bfcache, no cached device id)', {
              persisted: true,
              syncPayload: false,
              delivery: 'keepalive_async_fallback',
              idleSuspended: this.idleSuspended,
            });
            void this.sendFinalHeartbeat();
          }

          return;
        }

        const now = Date.now();
        if (shouldSkipRedundantUnloadFinal(now, this.lastPeriodicHeartbeatSentAtMs, this.intervalMs)) {
          if (!this.activitySinceLastPeriodic) {
            this.debug?.log('HeartbeatLoop', 'page:unload → skip redundant final (recent periodic, no activity)', {
              persisted: false,
              idleSuspended: this.idleSuspended,
            });

            return;
          }
          this.debug?.log('HeartbeatLoop', 'page:unload → final (recent periodic, had activity)', {
            persisted: false,
            idleSuspended: this.idleSuspended,
            delivery: 'keepalive',
          });
          void this.sendFinalHeartbeat();

          return;
        }

        this.debug?.log('HeartbeatLoop', 'page:unload → final (keepalive)', {
          persisted: false,
          delivery: 'keepalive',
          idleSuspended: this.idleSuspended,
        });
        void this.sendFinalHeartbeat();
      },
      TrackingTag.HeartbeatLoop,
    );
  }

  start(): void {
    if (this.running) {
      return;
    }
    this.running = true;
    this.paused = false;
    this.idleSuspended = false;
    this.unloadFinalPending = true;
    this.pageUnloadFinalSent = false;
    this.lastPeriodicHeartbeatSentAtMs = null;
    this.activitySinceLastPeriodic = false;
    this.debug?.log('HeartbeatLoop', 'start', {});
    void this.sendHeartbeat();
    this.scheduleNext();
  }

  stop(options?: { silent?: boolean }): void {
    if (!this.running) {
      return;
    }
    this.running = false;

    this.clearTimer();

    if (options?.silent) {
      this.unloadFinalPending = false;
      this.debug?.log('HeartbeatLoop', 'stop silent', {});

      return;
    }

    this.debug?.log('HeartbeatLoop', 'stop → final heartbeat', {});
    void this.sendFinalHeartbeat();
    this.unloadFinalPending = false;
  }

  /**
   * Schedules the next timer tick. Default base interval is `intervalMs`; after visibility resume with time
   * left in the current window, pass `baseIntervalMs = intervalMs - elapsed`.
   */
  private scheduleNext(options?: { baseIntervalMs?: number }): void {
    if (!this.running) {
      return;
    }
    if (this.paused) {
      return;
    }

    this.clearTimer();

    const base = options?.baseIntervalMs ?? this.intervalMs;
    const delay = heartbeatScheduleDelayMs(base, this.jitterMs);

    this.debug?.log('HeartbeatLoop', 'scheduleNext', {
      delayMs: delay,
      intervalMs: this.intervalMs,
      jitterMs: this.jitterMs,
      baseIntervalMs: base,
    });

    this.timerId = this.timers.setTimeout(() => {
      this.timerId = null;
      if (!this.running || this.paused) {
        return;
      }
      void this.onHeartbeatTimerTick();
    }, delay);
  }

  /** Tab visible again or idle suspend cleared — respect 30s cadence vs last periodic send. */
  private resumePeriodicCadenceAfterVisibility(): void {
    const decision = resumePeriodicCadenceDecision(Date.now(), this.lastPeriodicHeartbeatSentAtMs, this.intervalMs);
    if (decision.kind === 'send_then_schedule_full') {
      void this.sendHeartbeat();
      this.scheduleNext();

      return;
    }
    this.scheduleNext({ baseIntervalMs: decision.remainingMs });
  }

  /**
   * **Timer idle gate:** first thing on each heartbeat timer tick (when not paused).
   * If idle beyond threshold: rotate tab without emitting `tab:reset`, suspend periodic sends,
   * clear the outbound queue, stop scheduling — **no** `sendHeartbeat` this tick (so we do not ship one
   * more periodic collect after deciding the user is idle).
   */
  private async onHeartbeatTimerTick(): Promise<void> {
    if (this.tab.isBeyondIdleThreshold()) {
      this.tab.checkInactivityAndResetIfNeeded({ emitTabReset: false });
      this.idleSuspended = true;
      this.clearTimer();
      this.debug?.log('HeartbeatLoop', 'idle suspend (timer gate): timer stopped', {});

      return;
    }
    await this.sendHeartbeat();
    this.scheduleNext();
  }

  private clearTimer(): void {
    if (this.timerId == null) {
      return;
    }
    this.timers.clearTimeout(this.timerId);
    this.timerId = null;
  }

  /**
   * **Pre-send idle hook:** runs at the start of `buildPayload` / `buildPayloadSync` before reading tab id
   * or heartbeat index. If the idle threshold was exceeded, rotates tab storage without emitting
   * `tab:reset` (same as the timer gate), then the builder continues — so every outgoing payload matches
   * current storage. Covers timer-driven sends, tab resume, unload finals, and bfcache sync path.
   */
  private applyIdleHookBeforePayload(): void {
    this.tab.checkInactivityAndResetIfNeeded({ emitTabReset: false });
  }

  private buildPayloadSync(): HeartbeatPayload | null {
    this.applyIdleHookBeforePayload();
    const deviceId = this.device.getCachedDeviceId();
    if (!deviceId) {
      return null;
    }

    this.measurements.setViewportHeight(this.pageContext.get().viewport_height);
    const ctx = this.pageContext.get();

    return {
      meta: { version: this.schemaVersion },
      page: ctx.page,
      pageData: ctx.pageData,
      user: buildUserBlock(
        deviceId,
        this.tab.getTabId(),
        this.customer.getIdentity(),
        ctx,
      ),
      measurements: this.measurements.snapshot(),
      heartbeat: this.intervalMs,
      heartbeat_index: this.tab.getNextIndex(),
    };
  }

  private async buildPayload(): Promise<ReturnType<PayloadBuilder['build']>> {
    this.applyIdleHookBeforePayload();
    this.measurements.setViewportHeight(this.pageContext.get().viewport_height);

    return new PayloadBuilder()
      .withMeta(this.schemaVersion)
      .withPage(this.pageContext)
      .withUser(this.tab, this.customer, this.device)
      .withMeasurements(this.measurements.snapshot())
      .withHeartbeat(this.intervalMs, this.tab.getNextIndex())
      .build();
  }

  private async sendHeartbeat(): Promise<void> {
    const payload = await this.buildPayload();
    this.api.collect(payload);
    this.lastPeriodicHeartbeatSentAtMs = Date.now();
    this.activitySinceLastPeriodic = false;
  }

  private async sendFinalHeartbeat(): Promise<void> {
    const payload = await this.buildPayload();
    this.api.collect(payload, { final: true });
  }
}
